import React from 'react';
import { MessageCircle, Calendar, UserMinus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

interface VybeStatusCardProps {
  status: 'requested' | 'joined' | 'accepted' | 'confirmed' | 'completed' | 'cancelled';
  isBotVybe: boolean;
  eventDate: string;
  eventTime: string;
  onGoToChat?: () => void | Promise<void>;
  onWithdraw?: () => void | Promise<void>;
  isWithdrawing?: boolean;
  className?: string;
}

const VybeStatusCard: React.FC<VybeStatusCardProps> = ({
  status,
  isBotVybe,
  eventDate,
  eventTime,
  onGoToChat,
  onWithdraw,
  isWithdrawing = false,
  className = '',
}) => {
  const [isLoadingChat, setIsLoadingChat] = React.useState(false);

  const handleGoToChat = async () => {
    if (!onGoToChat) return;
    setIsLoadingChat(true);
    try {
      await onGoToChat();
    } catch (error) {
      console.error('Error navigating to chat:', error);
      setIsLoadingChat(false);
    }
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'requested':
        return {
          icon: '⏳',
          title: 'Request Submitted',
          message: 'The host will review your request and get back to you soon.',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/20',
          showActions: false,
        };
      case 'joined':
      case 'accepted':
        return {
          icon: '✅',
          title: "You're in!",
          message: 'Head to the group chat to coordinate the meet-up.',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/20',
          showActions: true,
        };
      case 'confirmed':
        return {
          icon: '🙌',
          title: 'Confirmed',
          message: 'See you there! Head to the group chat for final details.',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/20',
          showActions: true,
        };
      case 'completed':
        return {
          icon: '🎉',
          title: 'Vybe Complete',
          message: 'Hope you had a great time!',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/20',
          showActions: false,
        };
      case 'cancelled':
        return {
          icon: '❌',
          title: 'Vybe Cancelled',
          message: 'This Vybe has been cancelled.',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/20',
          showActions: false,
        };
      default:
        return {
          icon: '✅',
          title: "You're in!",
          message: 'Head to the group chat to coordinate the meet-up.',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500/20',
          showActions: true,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${config.bgColor} border ${config.borderColor} rounded-xl p-4 ${className}`}
    >
      {/* Status Header */}
      <div className="text-center mb-3">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: 'spring' }}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-white/10 to-white/5 mb-2"
        >
          <span className="text-2xl">{config.icon}</span>
        </motion.div>
        <h3 className="text-base font-bold text-white mb-1">{config.title}</h3>
        <p className="text-xs text-white/70">{config.message}</p>
      </div>

      {/* Event Details */}
      {status !== 'cancelled' && status !== 'completed' && (
        <div className="bg-white/5 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 text-white text-sm">
            <Calendar className="w-4 h-4 text-red-500" />
            <span className="font-semibold">{eventDate} · {eventTime}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {config.showActions && (
        <div className="space-y-2">
          {onGoToChat && (
            <button
              onClick={handleGoToChat}
              disabled={isLoadingChat}
              className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-full font-semibold text-sm transition-all shadow-lg shadow-pink-500/25 flex items-center justify-center gap-2"
            >
              {isLoadingChat ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opening chat...
                </>
              ) : (
                <>
                  <MessageCircle className="w-4 h-4" />
                  Go to Group Chat
                </>
              )}
            </button>
          )}

          {onWithdraw && (
            <button
              onClick={onWithdraw}
              disabled={isWithdrawing}
              className="w-full bg-white/5 hover:bg-white/10 border border-white/20 text-white/80 hover:text-white py-2.5 rounded-full font-medium text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWithdrawing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Withdrawing...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4" />
                  Withdraw from Vybe
                </>
              )}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default VybeStatusCard;
